import cx from 'classnames';
import { ControlProps } from 'components/ui';
import _ from 'lodash';
import { ChangeEvent, useState } from 'react';
import { Form } from './Form';
import css from './TimeInput.module.scss';

export interface TimeInputValue {
  hour: number;
  minutes: number;
}

const getDefaultControlValue = (value?: TimeInputValue) => {
  if (!value) {
    return '';
  }

  let hour = value.hour % 12;
  hour = hour === 0 ? 12 : hour;

  const minutes = _.padStart(_.toString(value.minutes), 2, '0');

  return `${hour}:${minutes}`;
};

const getDefaultIsAm = (value?: TimeInputValue) => {
  return value ? value?.hour < 12 : false;
};

interface TimeInputProps
  extends Omit<ControlProps, 'className' | 'value' | 'onChange'> {
  inputClassName?: string;
  onChange: (value?: TimeInputValue) => void;
  value: TimeInputValue;
  wrapperClassName?: string;
}

export const TimeInput = ({
  disabled,
  inputClassName,
  onChange,
  value,
  wrapperClassName: className,
  ...rest
}: TimeInputProps) => {
  const [controlValue, setControlValue] = useState<string>(
    getDefaultControlValue(value)
  );
  const [isAm, setAm] = useState<boolean>(getDefaultIsAm(value));

  const getIsInputValid = (value: string) => {
    const [hour, minutes] = _.split(value, ':');

    if (!/^(\d+)?:?(\d+)?/.test(value) || _.includes(value, '-')) {
      return false;
    }

    if (hour && (_.toInteger(hour) > 12 || _.toInteger(hour) < 1)) {
      return false;
    }

    if (
      minutes &&
      (_.size(minutes) > 2 ||
        _.toInteger(minutes) > 59 ||
        _.toInteger(minutes) < 0 ||
        // Prevents invalid partial time inputs like 12:7
        _.includes(['6', '7', '8', '9'], minutes))
    ) {
      return false;
    }

    return true;
  };

  const getHourValue = (hourInput: string, isAm: boolean) => {
    let hour = _.toInteger(hourInput);
    hour = hour === 12 ? 0 : hour;
    hour = isAm ? hour : hour + 12;
    return hour;
  };

  const getTimeInputValue = (
    inputValue: string,
    isAm: boolean
  ): TimeInputValue => {
    const [hour, minutes] = _.split(inputValue, ':');

    if (hour && minutes && _.size(minutes) === 2) {
      return {
        hour: getHourValue(hour, isAm),
        minutes: _.toInteger(minutes),
      };
    } else {
      return undefined;
    }
  };

  const handleChange = ({ target }: ChangeEvent<HTMLInputElement>) => {
    if (!target.value) {
      onChange(undefined);
      setControlValue('');
    } else if (getIsInputValid(target.value)) {
      onChange(getTimeInputValue(target.value, isAm));
      setControlValue(target.value);
    }
  };

  return (
    <div className={cx(css.timeInputWrapper, className)}>
      <Form.Control
        className={inputClassName}
        disabled={disabled}
        onChange={handleChange}
        value={controlValue}
        {...rest}
      />
      <input
        className={css.amPmButton}
        disabled={disabled}
        type="submit"
        onClick={(ev) => {
          setAm(!isAm);
          onChange(getTimeInputValue(controlValue, !isAm));
          ev.preventDefault();
          ev.stopPropagation();
        }}
        value={isAm ? 'AM' : 'PM'}
      />
    </div>
  );
};
